import React, { useState, useCallback, useRef } from 'react';
import {
  LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Activity, Clock, AlertTriangle, Users, Database, Zap,
  Server, ChevronDown, ChevronUp, Monitor, FileText
} from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import DashboardTile from '../../components/common/DashboardTile';
import { adminManagementApi } from '../../services/api';

// ── Period options ────────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { value: '1h',  label: '1 hr' },
  { value: '6h',  label: '6 hr' },
  { value: '24h', label: '24 hr' },
  { value: '7d',  label: '7 day' },
];

// ── Status / severity colors ──────────────────────────────────────────────────

const STATUS_COLORS = {
  '2xx':   '#059669',
  '3xx':   '#2563eb',
  '4xx':   '#f59e0b',
  '5xx':   '#dc2626',
  'other': '#94a3b8',
};

const SEV_COLORS = { 2: '#f59e0b', 3: '#dc2626', 4: '#7f1d1d' };
const SEV_LABELS = { 2: 'Warning', 3: 'Error', 4: 'Critical' };

// ── KPI descriptions (hover) ──────────────────────────────────────────────────

const DESCRIPTIONS = {
  requests:       'Total HTTP requests received by the API in the selected period.',
  avgResponse:    'Mean server response time across all requests.',
  p95Response:    '95th percentile response time — only 5% of requests were slower.',
  p99Response:    '99th percentile response time — worst-case experience for nearly all users.',
  errorRate:      'Percentage of requests that returned a non-success HTTP status code.',
  exceptionCount: 'Unhandled exceptions logged by the backend in the selected period.',
  sqlAvg:         'Average SQL query execution time across all dependency calls.',
  sqlP95:         '95th percentile SQL execution time — useful for catching slow query outliers.',
  sqlCalls:       'Total SQL dependency calls made by the backend.',
  sqlFails:       'SQL calls that returned an error during the selected period.',
  activeUsers:    'Distinct authenticated sessions observed in the last 24 hours.',
  pageViews:      'Total frontend page views tracked by the App Insights SDK.',
  avgPageLoad:    'Mean client-side page load time including network and render time.',
  browserTimings: 'Breakdown of client-side load phases: network, DOM processing, and total page load.',
};

// ── Collapsible section ───────────────────────────────────────────────────────

const Section = ({ id, title, icon: Icon, defaultOpen = true, children }) => {
  const key = `pmd-health-section-${id}`;
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(key);
    return saved !== null ? saved === 'true' : defaultOpen;
  });

  const toggle = () => {
    const next = !open;
    localStorage.setItem(key, next.toString());
    setOpen(next);
  };

  return (
    <>
      <div
        onClick={toggle}
        style={{
          gridColumn: 'span 6',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 4px 6px',
          cursor: 'pointer',
          borderBottom: '1px solid var(--color-border)',
          userSelect: 'none',
          color: 'var(--color-gray-500)',
          fontSize: 'var(--text-xs)',
          fontWeight: 600,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
        }}
      >
        {Icon && <Icon size={13} />}
        <span style={{ flex: 1 }}>{title}</span>
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </div>
      {open && children}
    </>
  );
};

// ── KPI tile with hover description ──────────────────────────────────────────

const KpiTile = ({ title, icon: Icon, colSpan = 1, descKey, children }) => {
  const [showDesc, setShowDesc] = useState(false);
  const timerRef = useRef(null);

  const handleMouseEnter = () => { timerRef.current = setTimeout(() => setShowDesc(true), 600); };
  const handleMouseLeave = () => { clearTimeout(timerRef.current); setShowDesc(false); };

  const style = colSpan !== 1 ? { gridColumn: `span ${colSpan}` } : {};

  return (
    <div className="dashboard-tile" style={style} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {(Icon || title) && (
        <div className="tile-header">
          {Icon && <Icon size={18} className="tile-icon" />}
          {title && <span className="tile-title">{title}</span>}
        </div>
      )}
      <div className="tile-content">{children}</div>
      <div style={{
        fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)',
        padding: '0 var(--spacing-sm) var(--spacing-sm)',
        textAlign: 'center', lineHeight: 1.4, minHeight: '1.4em',
        opacity: showDesc ? 1 : 0, transition: 'opacity 0.15s',
      }}>
        {DESCRIPTIONS[descKey]}
      </div>
    </div>
  );
};

// ── Metric display ────────────────────────────────────────────────────────────

const Metric = ({ value, label, color = 'var(--color-primary)', unit = '' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '110px', gap: '4px' }}>
    <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color }}>{value}{unit}</div>
    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>{label}</div>
  </div>
);

// ── Empty state ───────────────────────────────────────────────────────────────

const Empty = ({ msg = 'No data in this period' }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80px', color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)' }}>
    {msg}
  </div>
);

// ── Simple list for exceptions / endpoints ────────────────────────────────────

const DataList = ({ rows, cols }) => (
  <div style={{ padding: '4px 0' }}>
    {rows.length === 0
      ? <Empty />
      : rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', borderBottom: i < rows.length - 1 ? '1px solid var(--color-border)' : 'none', gap: '8px' }}>
          {cols.map((col, j) => (
            <span key={j} style={{ fontSize: 'var(--text-sm)', color: col.color?.(row) ?? 'var(--color-text)', flex: col.flex ?? 'unset', textAlign: col.align ?? 'left', fontWeight: col.bold ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {col.render ? col.render(row) : row[col.key]}
            </span>
          ))}
        </div>
      ))
    }
  </div>
);

// ── Distribution bar (browser/OS/device) ─────────────────────────────────────

const DistBar = ({ rows, keyProp, countProp = 'count' }) => {
  const total = rows.reduce((s, r) => s + (r[countProp] ?? 0), 0);
  if (!rows.length || total === 0) return <Empty />;
  return (
    <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {rows.map((r, i) => {
        const pct = total > 0 ? Math.round((r[countProp] / total) * 100) : 0;
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--color-gray-600)', marginBottom: '2px' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{r[keyProp] || 'Unknown'}</span>
              <span style={{ fontWeight: 600 }}>{pct}%</span>
            </div>
            <div style={{ height: '6px', background: 'var(--color-gray-100)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--color-primary)', borderRadius: '3px', transition: 'width 0.3s' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const SystemHealthPage = () => {
  const [period, setPeriod]   = useState('1h');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [data, setData]       = useState(null);
  const [dbSizes, setDbSizes] = useState([]);

  const loadData = useCallback(async (p) => {
    const sel = p ?? period;
    try {
      setLoading(true);
      setError(null);
      const [healthRes, sizesRes] = await Promise.all([
        adminManagementApi.getSystemHealth(sel),
        adminManagementApi.getDatabaseSizes(),
      ]);
      setData(healthRes.data);
      setDbSizes(sizesRes.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  const handlePeriodChange = (p) => { setPeriod(p); loadData(p); };

  const fmt = (n, dec = 1) =>
    n == null ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: dec });

  const errorColor   = (r) => !r ? 'var(--color-gray-400)' : r === 0 ? 'var(--color-success)' : r < 1 ? 'var(--color-warning)' : 'var(--color-danger)';
  const latencyColor = (ms, warn = 1000, crit = 2000) => ms > crit ? 'var(--color-danger)' : ms > warn ? 'var(--color-warning)' : 'var(--color-primary)';
  const shortName    = (name = '') => name.replace(/^(GET|POST|PUT|DELETE|PATCH) /, '').split('/').slice(-2).join('/');
  const shortType    = (type = '') => type.split('.').pop();
  const fmtTime      = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return period === '7d'
      ? d.toLocaleDateString([], { month: 'short', day: 'numeric' })
      : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const trendData    = data?.requestTrend?.map(r => ({ time: fmtTime(r.timestamp), count: r.count })) ?? [];
  const exTrendData  = data?.exceptionTrend?.map(r => ({ time: fmtTime(r.timestamp), count: r.count })) ?? [];
  const pvTrendData  = data?.pageViewTrend?.map(r => ({ time: fmtTime(r.timestamp), count: r.count })) ?? [];
  const endpointData = data?.slowestEndpoints?.map(r => ({ name: shortName(r.name), avgMs: r.avgMs, callCount: r.callCount })) ?? [];
  const sqlOpsData   = data?.sqlByOperation?.map(r => ({ name: shortName(r.name), avgMs: r.avgMs, p95Ms: r.p95Ms, callCount: r.callCount })) ?? [];
  const topPagesData = data?.topPages?.map(r => ({ name: shortName(r.name) || r.name, count: r.count, avgLoadMs: r.avgLoadMs })) ?? [];
  const statusData   = (() => {
    const map = Object.fromEntries((data?.statusBreakdown ?? []).map(s => [s.statusGroup, s.count]));
    return ['2xx', '3xx', '4xx', '5xx'].map(g => ({ group: g, count: map[g] ?? 0 }));
  })();

  // Perf buckets — sort by approximate response time bucket order
  const BUCKET_ORDER = ['<250ms','250ms-500ms','500ms-1s','1s-3s','3s-7s','>7s'];
  const perfBuckets  = (() => {
    const raw = data?.perfBuckets ?? [];
    const sorted = [...raw].sort((a, b) => {
      const ai = BUCKET_ORDER.indexOf(a.bucket);
      const bi = BUCKET_ORDER.indexOf(b.bucket);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
    return sorted.map(r => ({ bucket: r.bucket, count: r.count }));
  })();

  // Browser timings bar data
  const browserTimingData = data ? [
    { phase: 'Network', ms: data.browserNetworkMs ?? 0 },
    { phase: 'Send',    ms: data.browserSendMs ?? 0 },
    { phase: 'Receive', ms: data.browserReceiveMs ?? 0 },
    { phase: 'DOM',     ms: data.browserDomProcessMs ?? 0 },
    { phase: 'Total',   ms: data.browserDomLoadMs ?? 0 },
  ] : [];

  const recentLogs = data?.recentLogs ?? [];

  return (
    <DashboardLayout
      id="system-health"
      title="System Health"
      icon={Activity}
      onRefresh={() => loadData()}
      loading={loading}
      error={error}
    >
      {/* Period selector */}
      <div style={{ gridColumn: 'span 6', display: 'flex', alignItems: 'center', gap: '8px', padding: '2px 4px 6px' }}>
        {PERIOD_OPTIONS.map(opt => (
          <button key={opt.value} onClick={() => handlePeriodChange(opt.value)} style={{
            padding: '3px 14px', borderRadius: '4px', border: '1px solid var(--color-border)',
            background: period === opt.value ? 'var(--color-primary)' : 'transparent',
            color: period === opt.value ? '#fff' : 'var(--color-text)',
            cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: period === opt.value ? 600 : 400,
          }}>
            {opt.label}
          </button>
        ))}
        {data && !data.available && (
          <span style={{ marginLeft: '12px', fontSize: 'var(--text-sm)', color: 'var(--color-warning)' }}>
            ⚠ {data.reason}
          </span>
        )}
      </div>

      {data?.available && (
        <>
          {/* ── Request Performance ──────────────────────────────────────── */}
          <Section id="requests" title="Request Performance" icon={Activity}>
            <KpiTile title="Requests" icon={Zap} descKey="requests">
              <Metric value={fmt(data.requestCount, 0)} label="total" />
            </KpiTile>
            <KpiTile title="Avg Response" icon={Clock} descKey="avgResponse">
              <Metric value={fmt(data.avgResponseMs)} label="avg ms" unit=" ms" color={latencyColor(data.avgResponseMs, 300, 1000)} />
            </KpiTile>
            <KpiTile title="P95 Response" icon={Clock} descKey="p95Response">
              <Metric value={fmt(data.p95ResponseMs)} label="p95 ms" unit=" ms" color={latencyColor(data.p95ResponseMs)} />
            </KpiTile>
            <KpiTile title="P99 Response" icon={Clock} descKey="p99Response">
              <Metric value={fmt(data.p99ResponseMs)} label="p99 ms" unit=" ms" color={latencyColor(data.p99ResponseMs, 2000, 5000)} />
            </KpiTile>
            <KpiTile title="Error Rate" icon={AlertTriangle} descKey="errorRate">
              <Metric value={fmt(data.errorRate)} label="error %" unit="%" color={errorColor(data.errorRate)} />
            </KpiTile>

            <DashboardTile title="HTTP Status" icon={Activity} colSpan={2}>
              <ResponsiveContainer width="100%" height={110}>
                <BarChart data={statusData} margin={{ top: 8, right: 10, left: -20, bottom: 4 }}>
                  <XAxis dataKey="group" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v, n, p) => [v, p.payload.group]} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.group] ?? '#94a3b8'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </DashboardTile>

            <DashboardTile title="Request Trend" icon={Activity} colSpan={4}>
              <ResponsiveContainer width="100%" height={110}>
                <LineChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="var(--color-primary)" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </DashboardTile>

            <DashboardTile title="Slowest Endpoints" icon={Clock} colSpan={4}>
              {endpointData.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={endpointData} layout="vertical" margin={{ left: 10, right: 60, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} unit=" ms" />
                    <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`${v} ms`, 'Avg']} />
                    <Bar dataKey="avgMs" fill="var(--color-primary)" radius={[0, 4, 4, 0]}
                      label={{ position: 'right', fontSize: 11, formatter: v => `${v} ms` }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </DashboardTile>

            <DashboardTile title="Response Time Distribution" icon={Zap} colSpan={2}>
              {perfBuckets.length === 0
                ? <Empty msg="No perf bucket data" />
                : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={perfBuckets} margin={{ top: 5, right: 10, left: -20, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => [v, 'Requests']} />
                      <Bar dataKey="count" fill="var(--color-primary)" radius={[4, 4, 0, 0]}>
                        {perfBuckets.map((entry, i) => {
                          const idx = BUCKET_ORDER.indexOf(entry.bucket);
                          const colors = ['#059669','#2563eb','#f59e0b','#f97316','#dc2626','#7f1d1d'];
                          return <Cell key={i} fill={colors[idx] ?? '#94a3b8'} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )
              }
            </DashboardTile>
          </Section>

          {/* ── Errors & Exceptions ──────────────────────────────────────── */}
          <Section id="errors" title="Errors & Exceptions" icon={AlertTriangle}>
            <KpiTile title="Exceptions" icon={AlertTriangle} descKey="exceptionCount">
              <Metric
                value={fmt(data.exceptionCount, 0)} label="exceptions"
                color={data.exceptionCount > 0 ? 'var(--color-danger)' : 'var(--color-success)'}
              />
            </KpiTile>

            <DashboardTile title="Exception Types" icon={AlertTriangle} colSpan={3}>
              <DataList
                rows={data.topExceptions ?? []}
                cols={[
                  { key: 'type', flex: 1, render: r => shortType(r.type) },
                  { key: 'count', bold: true, align: 'right', color: () => 'var(--color-danger)' },
                ]}
              />
            </DashboardTile>

            <DashboardTile title="Exception Trend" icon={Activity} colSpan={2}>
              <ResponsiveContainer width="100%" height={110}>
                <LineChart data={exTrendData} margin={{ top: 5, right: 10, left: -20, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="var(--color-danger)" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </DashboardTile>

            <DashboardTile title="Failing Endpoints" icon={AlertTriangle} colSpan={6}>
              <DataList
                rows={data.failingEndpoints ?? []}
                cols={[
                  { key: 'name', flex: 1, render: r => shortName(r.name) },
                  { key: 'failCount', bold: true, align: 'right', color: () => 'var(--color-danger)', render: r => `${r.failCount} fails` },
                  { key: 'avgMs', align: 'right', color: () => 'var(--color-gray-400)', render: r => `${r.avgMs} ms avg` },
                ]}
              />
            </DashboardTile>
          </Section>

          {/* ── SQL / Dependencies ───────────────────────────────────────── */}
          <Section id="sql" title="SQL / Dependencies" icon={Database}>
            <KpiTile title="SQL Avg" icon={Database} descKey="sqlAvg">
              <Metric value={fmt(data.sqlAvgMs)} label="avg ms" unit=" ms" color={latencyColor(data.sqlAvgMs, 100, 500)} />
            </KpiTile>
            <KpiTile title="SQL P95" icon={Database} descKey="sqlP95">
              <Metric value={fmt(data.sqlP95Ms)} label="p95 ms" unit=" ms" color={latencyColor(data.sqlP95Ms, 500, 2000)} />
            </KpiTile>
            <KpiTile title="SQL Calls" icon={Database} descKey="sqlCalls">
              <Metric value={fmt(data.sqlCallCount, 0)} label="calls" />
            </KpiTile>
            <KpiTile title="SQL Failures" icon={AlertTriangle} descKey="sqlFails">
              <Metric
                value={fmt(data.sqlFailCount, 0)} label="failures"
                color={data.sqlFailCount > 0 ? 'var(--color-danger)' : 'var(--color-success)'}
              />
            </KpiTile>

            <DashboardTile title="SQL by Operation" icon={Database} colSpan={6}>
              {sqlOpsData.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={sqlOpsData} layout="vertical" margin={{ left: 10, right: 60, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} unit=" ms" />
                    <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, n) => [`${v} ms`, n === 'avgMs' ? 'Avg' : 'P95']} />
                    <Bar dataKey="avgMs" fill="var(--color-primary)" radius={[0, 4, 4, 0]}
                      label={{ position: 'right', fontSize: 11, formatter: v => `${v} ms` }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </DashboardTile>
          </Section>

          {/* ── Frontend & Users ─────────────────────────────────────────── */}
          <Section id="frontend" title="Frontend & Users" icon={Users}>
            <KpiTile title="Active Users" icon={Users} colSpan={2} descKey="activeUsers">
              <Metric value={fmt(data.activeUsers24h, 0)} label="last 24 h" color="var(--color-success)" />
            </KpiTile>
            <KpiTile title="Page Views" icon={Activity} colSpan={2} descKey="pageViews">
              <Metric value={fmt(data.totalPageViews, 0)} label="page views" color="var(--color-primary)" />
            </KpiTile>
            <KpiTile title="Avg Page Load" icon={Clock} colSpan={2} descKey="avgPageLoad">
              <Metric value={fmt(data.avgPageLoadMs, 0)} label="ms load time" unit=" ms" color={latencyColor(data.avgPageLoadMs, 2000, 5000)} />
            </KpiTile>

            {/* Browser / OS / Device row */}
            <DashboardTile title="Browser" icon={Monitor} colSpan={2}>
              <DistBar rows={data.browserBreakdown ?? []} keyProp="browser" />
            </DashboardTile>
            <DashboardTile title="Operating System" icon={Monitor} colSpan={2}>
              <DistBar rows={data.osBreakdown ?? []} keyProp="os" />
            </DashboardTile>
            <DashboardTile title="Device Type" icon={Monitor} colSpan={2}>
              <DistBar rows={data.deviceBreakdown ?? []} keyProp="device" />
            </DashboardTile>

            {/* Browser load phase breakdown */}
            <KpiTile title="Browser Load Phases" icon={Clock} colSpan={3} descKey="browserTimings">
              {browserTimingData.every(d => d.ms === 0)
                ? <Empty msg="No browser timing data" />
                : (
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={browserTimingData} margin={{ top: 8, right: 10, left: -10, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="phase" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 10 }} unit=" ms" />
                      <Tooltip formatter={(v) => [`${v} ms`, 'Avg']} />
                      <Bar dataKey="ms" fill="var(--color-primary)" radius={[4, 4, 0, 0]}
                        label={{ position: 'top', fontSize: 10, formatter: v => v > 0 ? `${Math.round(v)}` : '' }} />
                    </BarChart>
                  </ResponsiveContainer>
                )
              }
            </KpiTile>

            <DashboardTile title="Top Pages" icon={Activity} colSpan={3}>
              {topPagesData.length === 0
                ? <Empty msg="No page view data — SDK may still be warming up" />
                : (
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={topPagesData} layout="vertical" margin={{ left: 10, right: 40, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => [v, 'Views']} />
                      <Bar dataKey="count" fill="var(--color-success)" radius={[0, 4, 4, 0]}
                        label={{ position: 'right', fontSize: 10 }} />
                    </BarChart>
                  </ResponsiveContainer>
                )
              }
            </DashboardTile>

            <DashboardTile title="Page View Trend" icon={Activity} colSpan={6}>
              {pvTrendData.length === 0 ? <Empty msg="No page view data yet" /> : (
                <ResponsiveContainer width="100%" height={110}>
                  <LineChart data={pvTrendData} margin={{ top: 5, right: 10, left: -20, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="var(--color-success)" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </DashboardTile>
          </Section>

          {/* ── Application Logs ─────────────────────────────────────────── */}
          <Section id="logs" title="Application Logs" icon={FileText} defaultOpen={false}>
            <div style={{ gridColumn: 'span 6' }}>
              {recentLogs.length === 0
                ? (
                  <div className="dashboard-tile" style={{ gridColumn: 'span 6' }}>
                    <Empty msg="No warnings or errors in the last 24 hours" />
                  </div>
                )
                : (
                  <div className="dashboard-tile" style={{ gridColumn: 'span 6', padding: '0' }}>
                    <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)', display: 'flex', gap: '12px', fontSize: '11px', fontWeight: 600, color: 'var(--color-gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <span style={{ width: '140px' }}>Time</span>
                      <span style={{ width: '70px' }}>Severity</span>
                      <span style={{ flex: 1 }}>Message</span>
                      <span style={{ width: '140px' }}>Operation</span>
                    </div>
                    {recentLogs.map((log, i) => {
                      const sev = log.severityLevel ?? 2;
                      const sevColor = SEV_COLORS[sev] ?? '#94a3b8';
                      const sevLabel = SEV_LABELS[sev] ?? `Level ${sev}`;
                      const ts = log.timestamp ? new Date(log.timestamp).toLocaleTimeString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
                      return (
                        <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '6px 12px', borderBottom: i < recentLogs.length - 1 ? '1px solid var(--color-gray-100)' : 'none', background: sev >= 3 ? `${sevColor}08` : 'transparent' }}>
                          <span style={{ width: '140px', fontSize: '11px', color: 'var(--color-gray-500)', whiteSpace: 'nowrap', flexShrink: 0 }}>{ts}</span>
                          <span style={{ width: '70px', fontSize: '11px', fontWeight: 600, color: sevColor, flexShrink: 0 }}>{sevLabel}</span>
                          <span style={{ flex: 1, fontSize: '12px', color: 'var(--color-gray-700)', wordBreak: 'break-word', lineHeight: 1.4 }}>{log.message}</span>
                          <span style={{ width: '140px', fontSize: '11px', color: 'var(--color-gray-400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'right' }}>{log.operation}</span>
                        </div>
                      );
                    })}
                  </div>
                )
              }
            </div>
          </Section>

          {/* ── Infrastructure ───────────────────────────────────────────── */}
          <Section id="infra" title="Infrastructure" icon={Server}>
            <DashboardTile title="Database Sizes" icon={Database} colSpan={6}>
              {dbSizes.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={Math.max(100, dbSizes.length * 48)}>
                  <BarChart data={dbSizes} margin={{ top: 5, right: 10, left: -20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} unit=" MB" />
                    <Tooltip formatter={(v) => [`${v} MB`, 'Allocated']} />
                    <Bar dataKey="sizeMb" fill="var(--color-primary)" radius={[4, 4, 0, 0]}
                      label={{ position: 'top', fontSize: 11, formatter: v => `${v} MB` }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </DashboardTile>
          </Section>
        </>
      )}
    </DashboardLayout>
  );
};

export default SystemHealthPage;
